import { useState } from "react";
import { trpc } from "../../lib/trpc";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Eye,
  Search,
  DollarSign,
  Tag,
  Users,
  Clock,
  Save,
  Building,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ReceiptViewerModal } from "../../components/billing/ReceiptViewerModal";
import {
  type BillingPlansConfig,
  DEFAULT_BILLING_CONFIG,
} from "@profolio/validators";

export function AdminDashboard() {
  const trpcContext = trpc.useUtils();
  const [activeTab, setActiveTab] = useState<
    "pending" | "pricing" | "promotions" | "directory"
  >("pending");

  // Queries
  const { data: pendingSubscriptions, isLoading: isPendingLoading } =
    trpc.admin.getPendingSubscriptions.useQuery(undefined, {
      refetchInterval: 5000,
    });

  const { data: billingConfig } =
    trpc.admin.getBillingConfig.useQuery();

  // Search & Filters for Subscriptions Directory
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");

  const { data: allSubscriptions, isLoading: isAllSubsLoading } =
    trpc.admin.getAllSubscriptions.useQuery({
      search: searchQuery || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      tier: tierFilter !== "all" ? tierFilter : undefined,
    });

  // Approvals & Rejection State
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approvalDurations, setApprovalDurations] = useState<Record<string, number>>({});
  const [activeReceiptUrl, setActiveReceiptUrl] = useState<string | null>(null);
  const [activeReceiptMeta, setActiveReceiptMeta] = useState<any>(null);

  // Editable Pricing Form State
  const [pricingForm, setPricingForm] = useState<BillingPlansConfig | null>(null);

  // Sync pricingForm when billingConfig loads
  const currentConfig: BillingPlansConfig = pricingForm || billingConfig || DEFAULT_BILLING_CONFIG;

  const handlePriceChange = (tier: "pro" | "premium", field: "monthlyPrice" | "annualPrice", val: number) => {
    setPricingForm((prev) => {
      const base = prev || billingConfig || DEFAULT_BILLING_CONFIG;
      return {
        ...base,
        plans: {
          ...base.plans,
          [tier]: {
            ...base.plans[tier],
            [field]: val,
          },
        },
      };
    });
  };

  const handleBankDetailChange = (field: keyof BillingPlansConfig["bankDetails"], val: string) => {
    setPricingForm((prev) => {
      const base = prev || billingConfig || DEFAULT_BILLING_CONFIG;
      return {
        ...base,
        bankDetails: {
          ...base.bankDetails,
          [field]: val,
        },
      };
    });
  };

  const handleOfferChange = (field: string, val: any) => {
    setPricingForm((prev) => {
      const base = prev || billingConfig || DEFAULT_BILLING_CONFIG;
      return {
        ...base,
        activeOffer: {
          enabled: base.activeOffer?.enabled ?? false,
          discountPercent: base.activeOffer?.discountPercent ?? 0,
          badgeText: base.activeOffer?.badgeText ?? "",
          title: base.activeOffer?.title ?? "",
          description: base.activeOffer?.description ?? "",
          ...base.activeOffer,
          [field]: val,
        },
      };
    });
  };

  // Mutations
  const approveMutation = trpc.admin.approveSubscription.useMutation({
    onSuccess: () => {
      toast.success("Subscription approved successfully");
      trpcContext.admin.getPendingSubscriptions.invalidate();
      trpcContext.admin.getAllSubscriptions.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const rejectMutation = trpc.admin.rejectSubscription.useMutation({
    onSuccess: () => {
      toast.success("Subscription rejected");
      trpcContext.admin.getPendingSubscriptions.invalidate();
      trpcContext.admin.getAllSubscriptions.invalidate();
      setRejectId(null);
      setRejectReason("");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const updateConfigMutation = trpc.admin.updateBillingConfig.useMutation({
    onSuccess: () => {
      toast.success("Billing configuration and pricing updated successfully");
      trpcContext.admin.getBillingConfig.invalidate();
      trpcContext.billing.getBillingConfig.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleSaveConfig = () => {
    if (!currentConfig) return;
    updateConfigMutation.mutate(currentConfig);
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto space-y-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
            Platform Operations Center
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Manual bank slip verification, live plan pricing, promotional discounts, and subscriber directory.
          </p>
        </div>

        {/* Tab Navigation Controls */}
        <div className="flex items-center gap-1.5 p-1 bg-muted rounded-xl text-xs font-semibold self-start sm:self-auto overflow-x-auto max-w-full">
          <button
            type="button"
            onClick={() => setActiveTab("pending")}
            className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === "pending"
                ? "bg-card text-foreground shadow-xs font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Clock className="h-3.5 w-3.5" />
            <span>Pending Approvals</span>
            {pendingSubscriptions && pendingSubscriptions.length > 0 && (
              <span className="h-4 px-1.5 text-[10px] rounded-full bg-amber-500 text-white font-extrabold flex items-center justify-center">
                {pendingSubscriptions.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("pricing")}
            className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === "pricing"
                ? "bg-card text-foreground shadow-xs font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <DollarSign className="h-3.5 w-3.5" />
            <span>Plan Pricing</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("promotions")}
            className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === "promotions"
                ? "bg-card text-foreground shadow-xs font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Tag className="h-3.5 w-3.5" />
            <span>Promotions</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("directory")}
            className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${
              activeTab === "directory"
                ? "bg-card text-foreground shadow-xs font-bold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            <span>Subscriptions Directory</span>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 1: PENDING APPROVALS QUEUE (ENRICHED APPLICANT CONTEXT + LIGHTBOX)
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "pending" && (
        <div className="bg-card rounded-2xl border border-border shadow-xs overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-muted/20 flex items-center justify-between">
            <h2 className="text-sm font-bold text-foreground">Pending Manual Bank Transfers</h2>
            <span className="text-xs text-muted-foreground">
              {pendingSubscriptions?.length || 0} awaiting verification
            </span>
          </div>

          {isPendingLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : !pendingSubscriptions || pendingSubscriptions.length === 0 ? (
            <div className="p-12 text-center text-xs text-muted-foreground">
              No pending bank transfers in queue.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {pendingSubscriptions.map((sub) => {
                const meta = (sub.receiptMetadata as any) || {};
                const duration =
                  approvalDurations[sub.id] ?? (sub.billingCycle === "annual" ? 365 : 30);

                return (
                  <div
                    key={sub.id}
                    className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 hover:bg-muted/10 transition-colors"
                  >
                    {/* Applicant & Request Context */}
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-foreground">
                          {sub.userName || "Unnamed Scholar"}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">
                          ({sub.userEmail || "No email"})
                        </span>
                        {sub.subdomain && (
                          <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-muted text-foreground">
                            {sub.subdomain}.profolio.lk
                          </span>
                        )}
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {sub.tier} • {sub.billingCycle || "Monthly"}
                        </span>
                      </div>

                      {/* Bank Receipt Metadata */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
                        <div>
                          <span className="text-[10px] uppercase font-semibold block">Reference #:</span>
                          <span className="font-mono font-bold text-foreground">
                            {meta.referenceNumber || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-semibold block">Depositor:</span>
                          <span className="font-semibold text-foreground">
                            {meta.depositorName || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-semibold block">Requested:</span>
                          <span>{new Date(sub.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {meta.notes && (
                        <p className="text-[11px] text-muted-foreground italic bg-muted/30 p-2 rounded-md">
                          Note: "{meta.notes}"
                        </p>
                      )}
                    </div>

                    {/* Receipt Preview & Decision Actions */}
                    <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
                      {sub.paymentReceiptUrl ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActiveReceiptUrl(sub.paymentReceiptUrl);
                            setActiveReceiptMeta({
                              referenceNumber: meta.referenceNumber,
                              depositorName: meta.depositorName,
                              amount: meta.amount,
                              currency: meta.currency,
                              transferDate: meta.transferDate,
                            });
                          }}
                          className="text-xs gap-1.5 h-8 font-semibold"
                        >
                          <Eye className="h-3.5 w-3.5 text-primary" />
                          <span>View Slip</span>
                        </Button>
                      ) : (
                        <span className="text-xs text-destructive font-medium">No Receipt</span>
                      )}

                      {/* Duration selector */}
                      <select
                        value={duration}
                        onChange={(e) =>
                          setApprovalDurations((prev) => ({
                            ...prev,
                            [sub.id]: Number(e.target.value),
                          }))
                        }
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs font-semibold"
                      >
                        <option value={30}>30 Days (1 Month)</option>
                        <option value={60}>60 Days (2 Months)</option>
                        <option value={90}>90 Days (3 Months)</option>
                        <option value={365}>365 Days (1 Year)</option>
                      </select>

                      <Button
                        size="sm"
                        disabled={approveMutation.isPending}
                        onClick={() =>
                          approveMutation.mutate({
                            subscriptionId: sub.id,
                            durationDays: duration,
                          })
                        }
                        className="text-xs h-8 gap-1 font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Approve</span>
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectId(sub.id)}
                        className="text-xs h-8 text-destructive hover:bg-destructive/10 hover:border-destructive/40"
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                        <span>Reject</span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 2: PLAN PRICING MANAGEMENT (DYNAMIC SITE_SETTINGS BACKED)
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "pricing" && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border shadow-xs p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div>
                <h2 className="text-sm font-bold text-foreground">Membership Plan Rates</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Update live subscription prices. Changes take effect immediately across all client checkout flows.
                </p>
              </div>
              <Button
                onClick={handleSaveConfig}
                disabled={updateConfigMutation.isPending}
                className="text-xs font-bold gap-2"
              >
                {updateConfigMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                <span>Save All Changes</span>
              </Button>
            </div>

            {/* Currency */}
            <div className="max-w-xs space-y-1">
              <label className="text-xs font-semibold text-foreground block">Platform Currency</label>
              <select
                value={currentConfig.currency}
                onChange={(e) =>
                  setPricingForm((prev) => ({
                    ...(prev || currentConfig),
                    currency: e.target.value as any,
                  }))
                }
                className="w-full p-2 rounded-lg border border-border bg-background text-xs font-semibold"
              >
                <option value="LKR">LKR (Sri Lankan Rupee)</option>
                <option value="USD">USD (US Dollar)</option>
              </select>
            </div>

            {/* Plan Price Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pro Plan */}
              <div className="p-5 rounded-xl border border-border bg-muted/20 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">Pro Plan</h3>
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    Most Popular
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground block">
                      Monthly Price ({currentConfig.currency})
                    </label>
                    <input
                      type="number"
                      value={currentConfig.plans.pro.monthlyPrice}
                      onChange={(e) => handlePriceChange("pro", "monthlyPrice", Number(e.target.value))}
                      className="w-full p-2.5 rounded-lg border border-border bg-background text-xs font-mono font-bold"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground block">
                      Annual Price ({currentConfig.currency})
                    </label>
                    <input
                      type="number"
                      value={currentConfig.plans.pro.annualPrice}
                      onChange={(e) => handlePriceChange("pro", "annualPrice", Number(e.target.value))}
                      className="w-full p-2.5 rounded-lg border border-border bg-background text-xs font-mono font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="p-5 rounded-xl border border-border bg-muted/20 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-foreground">Premium Plan</h3>
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
                    Luxury Peak
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground block">
                      Monthly Price ({currentConfig.currency})
                    </label>
                    <input
                      type="number"
                      value={currentConfig.plans.premium.monthlyPrice}
                      onChange={(e) => handlePriceChange("premium", "monthlyPrice", Number(e.target.value))}
                      className="w-full p-2.5 rounded-lg border border-border bg-background text-xs font-mono font-bold"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground block">
                      Annual Price ({currentConfig.currency})
                    </label>
                    <input
                      type="number"
                      value={currentConfig.plans.premium.annualPrice}
                      onChange={(e) => handlePriceChange("premium", "annualPrice", Number(e.target.value))}
                      className="w-full p-2.5 rounded-lg border border-border bg-background text-xs font-mono font-bold"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Deposit Account Configuration */}
            <div className="pt-4 border-t border-border space-y-4">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <Building className="h-4 w-4 text-primary" />
                <span>Bank Deposit Account Details</span>
              </h3>
              <p className="text-xs text-muted-foreground">
                Displayed to users who opt for manual bank transfer on the checkout page.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground block">Bank Name</label>
                  <input
                    type="text"
                    value={currentConfig.bankDetails.bankName}
                    onChange={(e) => handleBankDetailChange("bankName", e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground block">Branch Name</label>
                  <input
                    type="text"
                    value={currentConfig.bankDetails.branch}
                    onChange={(e) => handleBankDetailChange("branch", e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground block">Account Holder Name</label>
                  <input
                    type="text"
                    value={currentConfig.bankDetails.accountName}
                    onChange={(e) => handleBankDetailChange("accountName", e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground block">Account Number</label>
                  <input
                    type="text"
                    value={currentConfig.bankDetails.accountNumber}
                    onChange={(e) => handleBankDetailChange("accountNumber", e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs font-mono font-bold"
                  />
                </div>
                <div className="sm:col-span-2 space-y-1">
                  <label className="font-semibold text-muted-foreground block">Deposit Instructions</label>
                  <input
                    type="text"
                    value={currentConfig.bankDetails.instructions || ""}
                    onChange={(e) => handleBankDetailChange("instructions", e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                    placeholder="e.g. Please put your username or email as the payment reference."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 3: PROMOTIONAL CAMPAIGNS ENGINE
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "promotions" && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl border border-border shadow-xs p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-border">
              <div>
                <h2 className="text-sm font-bold text-foreground">Promotional Campaign Settings</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Launch discounts and sales banners visible across all billing and checkout interfaces.
                </p>
              </div>
              <Button
                onClick={handleSaveConfig}
                disabled={updateConfigMutation.isPending}
                className="text-xs font-bold gap-2"
              >
                {updateConfigMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                <span>Save Promotion</span>
              </Button>
            </div>

            {/* Campaign Toggle & Discount Rate */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="p-4 rounded-xl border border-border bg-muted/20 flex items-center justify-between">
                <div>
                  <span className="font-bold text-sm text-foreground block">Campaign Status</span>
                  <span className="text-xs text-muted-foreground">
                    {currentConfig.activeOffer?.enabled
                      ? "Campaign is currently LIVE"
                      : "Campaign is currently INACTIVE"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleOfferChange("enabled", !currentConfig.activeOffer?.enabled)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    currentConfig.activeOffer?.enabled
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/30"
                  }`}
                >
                  {currentConfig.activeOffer?.enabled ? "Active (Disable)" : "Disabled (Enable)"}
                </button>
              </div>

              <div className="p-4 rounded-xl border border-border bg-muted/20 space-y-1">
                <label className="text-xs font-bold text-foreground flex items-center justify-between">
                  <span>Discount Percentage</span>
                  <span className="font-mono text-primary font-black">
                    {currentConfig.activeOffer?.discountPercent ?? 0}% OFF
                  </span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={currentConfig.activeOffer?.discountPercent ?? 0}
                  onChange={(e) => handleOfferChange("discountPercent", Number(e.target.value))}
                  className="w-full accent-primary cursor-pointer"
                />
              </div>
            </div>

            {/* Campaign Copy */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground block">Badge Label</label>
                <input
                  type="text"
                  placeholder="e.g. LAUNCH 20% OFF or ACADEMIC YEAR SALE"
                  value={currentConfig.activeOffer?.badgeText || ""}
                  onChange={(e) => handleOfferChange("badgeText", e.target.value)}
                  className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground block">Campaign Title</label>
                <input
                  type="text"
                  placeholder="e.g. Celebration Launch Special"
                  value={currentConfig.activeOffer?.title || ""}
                  onChange={(e) => handleOfferChange("title", e.target.value)}
                  className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <label className="font-semibold text-muted-foreground block">Description Text</label>
                <input
                  type="text"
                  placeholder="e.g. Enjoy 20% off all Pro & Premium annual and monthly plans for a limited time."
                  value={currentConfig.activeOffer?.description || ""}
                  onChange={(e) => handleOfferChange("description", e.target.value)}
                  className="w-full p-2.5 rounded-lg border border-border bg-background text-xs"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <label className="font-semibold text-muted-foreground block">Valid Until Date (Optional)</label>
                <input
                  type="date"
                  value={currentConfig.activeOffer?.validUntil?.split("T")[0] || ""}
                  onChange={(e) => handleOfferChange("validUntil", e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                  className="w-full p-2.5 rounded-lg border border-border bg-background text-xs max-w-xs"
                />
              </div>
            </div>

            {/* Live Visual Preview */}
            <div className="pt-4 border-t border-border space-y-2">
              <span className="text-[10px] uppercase font-bold text-muted-foreground block">
                Live User Banner Preview
              </span>
              <div className="rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-amber-500/10 to-primary/5 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
                    <Tag className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                        {currentConfig.activeOffer?.badgeText || "SPECIAL OFFER"}
                      </span>
                      <span className="text-xs font-bold text-foreground">
                        {currentConfig.activeOffer?.title || "Campaign Headline"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {currentConfig.activeOffer?.description ||
                        `Save ${currentConfig.activeOffer?.discountPercent ?? 0}% on all subscriptions.`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold text-primary">
                    {currentConfig.activeOffer?.discountPercent ?? 0}% OFF
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB 4: SUBSCRIPTIONS DIRECTORY
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "directory" && (
        <div className="bg-card rounded-2xl border border-border shadow-xs overflow-hidden space-y-4">
          <div className="p-4 border-b border-border bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search email, name, subdomain..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-hidden"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 self-start sm:self-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 rounded-lg border border-border bg-background px-2 text-xs"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>

              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="h-8 rounded-lg border border-border bg-background px-2 text-xs"
              >
                <option value="all">All Tiers</option>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {isAllSubsLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : !allSubscriptions || allSubscriptions.length === 0 ? (
            <div className="p-12 text-center text-xs text-muted-foreground">
              No subscription records matched your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-muted/40 text-[11px] uppercase font-bold text-muted-foreground border-b border-border">
                  <tr>
                    <th className="py-2.5 px-4">User</th>
                    <th className="py-2.5 px-4">Subdomain</th>
                    <th className="py-2.5 px-4">Tier & Cycle</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4">Expires</th>
                    <th className="py-2.5 px-4">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {allSubscriptions.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/20 transition-colors">
                      <td className="py-3 px-4 font-medium text-foreground">
                        <div>{item.userName || "Unnamed User"}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{item.userEmail}</div>
                      </td>
                      <td className="py-3 px-4 font-mono text-[11px] text-muted-foreground">
                        {item.subdomain ? `${item.subdomain}.profolio.lk` : "—"}
                      </td>
                      <td className="py-3 px-4 capitalize font-semibold">
                        {item.tier} • {item.billingCycle || "Monthly"}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            item.status === "active"
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                              : item.status === "pending_approval"
                              ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
                              : item.status === "expired"
                              ? "bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {item.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : "Lifetime / None"}
                      </td>
                      <td className="py-3 px-4">
                        {item.paymentReceiptUrl ? (
                          <button
                            type="button"
                            onClick={() => {
                              setActiveReceiptUrl(item.paymentReceiptUrl);
                              setActiveReceiptMeta(item.receiptMetadata);
                            }}
                            className="text-primary hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                          >
                            <Eye className="h-3 w-3" /> Slip
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Rejection Modal ── */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="text-sm font-bold text-foreground">Reject Bank Deposit Slip</h3>
            <p className="text-xs text-muted-foreground">
              Provide a clear reason for the rejection. This note will be shown directly to the user on their billing page so they can re-upload.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full min-h-[90px] p-3 border border-border rounded-lg text-xs bg-background text-foreground focus:outline-hidden"
              placeholder="e.g. Deposit amount does not match plan price, or slip image is unreadable."
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setRejectId(null)} className="text-xs">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  rejectMutation.mutate({
                    subscriptionId: rejectId,
                    reason: rejectReason,
                  })
                }
                disabled={!rejectReason.trim() || rejectMutation.isPending}
                className="text-xs font-bold bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                {rejectMutation.isPending ? "Rejecting..." : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── In-App Lightbox Modal ── */}
      <ReceiptViewerModal
        open={Boolean(activeReceiptUrl)}
        onOpenChange={(open) => !open && setActiveReceiptUrl(null)}
        receiptUrl={activeReceiptUrl}
        metadata={activeReceiptMeta}
      />
    </div>
  );
}
